
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import { useFiscalYear } from '../../../shared/FiscalYearContext';
import { useCheckInData, useSaveCheckIn } from '../services/checkInService';
import { CHECKIN_STATUS, CHECKIN_PERIODS, STATUS_CONFIG } from '../utils/checkInConstants';
import ObjectiveCard from './ObjectiveCard';
import OverallSummary from './OverallSummary';
import ManagerReviewPanel from './ManagerReviewPanel';
import { 
  IconArrowLeft16, 
  IconSave16, 
  IconCheckmark16, 
  IconWarning16, 
  IconUserGroup16, 
  IconCalendar16, 
  IconClock16 
} from '@dhis2/ui';
import ToastNotification from '../../../shared/components/ToastNotification';
import { GiEyeball, GiNotebook } from 'react-icons/gi';
import './CheckInForm.css';
import { useInitiateCheckIn } from '../services/checkInService';
import { sendCheckInInitiatedEmail,
         sendCheckInSubmittedEmail,
         sendCheckInReviewedEmail,
         sendMeetingScheduledEmail } from '../services/checkInEmails';
import { AlertBar } from '@dhis2/ui';


const CheckInForm = () => {
  const { employeeId, period } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { fiscalYear, loading: fiscalYearLoading } = useFiscalYear();
  const { initiateCheckIn } = useInitiateCheckIn();
  const mode = searchParams.get('mode') || 'employee';
  const isManagerView = mode === 'manager';
  
  const [checkInData, setCheckInData] = useState(null);
  const [expandedObjectiveId, setExpandedObjectiveId] = useState(null);
  const [toast, setToast] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [showMeetingScheduler, setShowMeetingScheduler] = useState(false);
  const [meetingData, setMeetingData] = useState({ date: '', notes: '' });

  const getFinancialYear = useCallback(() => {
    if (fiscalYear?.start && fiscalYear?.end) {
      const startYear = new Date(fiscalYear.start).getFullYear();
      const endYear = new Date(fiscalYear.end).getFullYear();
      return `${startYear}-${endYear}`;
    }
    const currentYear = new Date().getFullYear();
    return `${currentYear}-${currentYear + 1}`;
  }, [fiscalYear]);

  const financialYear = getFinancialYear();
  const orgUnit = user?.organisationUnits?.[0]?.id;

  const { data, loading, error, refetch } = useCheckInData(employeeId, period, financialYear, orgUnit, fiscalYear);
  const { saveCheckIn } = useSaveCheckIn();

  const handleObjectiveChange = useCallback((objectiveId, field, value) => {
    setCheckInData(prev => prev ? { ...prev, objectives: prev.objectives.map(obj => obj.id === objectiveId ? { ...obj, [field]: value } : obj) } : prev);
  }, []);

  const handleOverallChange = useCallback((field, value) => {
    setCheckInData(prev => prev ? { ...prev, overall: { ...prev.overall, [field]: value } } : prev);
  }, []);

  const toggleObjectiveExpand = useCallback((objectiveId) => {
    setExpandedObjectiveId(prev => (prev === objectiveId ? null : objectiveId));
  }, []);

  const stableData = useMemo(() => data, [data]);

  useEffect(() => {
    // ✅ Only update state when loading is finished and we have valid data
    if (!loading && data && !isDataLoaded) {
      setCheckInData(data);
      setIsDataLoaded(true);
    }
  }, [loading, data, isDataLoaded]);

  useEffect(() => {
    setIsDataLoaded(false);
    setCheckInData(null);
    setExpandedObjectiveId(null);
  }, [employeeId, period, financialYear, orgUnit]);

    const handleSaveDraft = async () => {
      if (!checkInData) return;
      setIsSaving(true);
      try {
        await saveCheckIn(checkInData, CHECKIN_STATUS.DRAFT);

        // ✅ Instantly update local status so the timeline + badge refresh now
        setCheckInData(prev => (prev ? { ...prev, status: CHECKIN_STATUS.DRAFT } : prev));

        setToast({ type: 'success', message: 'Check-in saved as draft' });
        refetch();
      } catch (error) {
        setToast({ type: 'error', message: 'Failed to save draft' });
      } finally {
        setIsSaving(false);
      }
    };

  const handleSubmitFinal = async () => {
    if (!checkInData || !checkInData.objectives || checkInData.objectives.length === 0) {
      setToast({ type: 'critical', message: 'No objectives found to submit.' });
      return;
    }

    // 1. COMPREHENSIVE VALIDATION LOOP
    for (let i = 0; i < checkInData.objectives.length; i++) {
      const obj = checkInData.objectives[i];
      const objName = obj.text || `Objective ${i + 1}`;

      if (!obj.progressStatus) {
        setToast({ type: 'warning', message: `${objName}: Please select a Progress Status.` });
        return; 
      }
      if (obj.percentComplete === undefined || obj.percentComplete === null || obj.percentComplete === '') {
        setToast({ type: 'warning', message: `${objName}: Please provide the Percent Complete.` });
        return;
      }
      if (!obj.employeeComments || obj.employeeComments.trim() === '') {
        setToast({ type: 'warning', message: `${objName}: Please provide Your Comments.` });
        return;
      }
      if (!obj.supportNeeds || obj.supportNeeds.trim() === '') {
        setToast({ type: 'warning', message: `${objName}: Please provide Support Needs.` });
        return;
      }
    }

    // 2. IF ALL VALIDATIONS PASS, PROCEED WITH SAVE
    const overall = checkInData.overall || {};
    
    if (!overall.achievements || overall.achievements.trim() === '') {
      setToast({ type: 'warning', message: 'Overall Summary: Please provide your Key Achievements.' });
      // Optional: Scroll to the overall summary section so the user sees it immediately
      document.getElementById('overall-summary-section')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    
    if (!overall.challenges || overall.challenges.trim() === '') {
      setToast({ type: 'warning', message: 'Overall Summary: Please provide your Challenges.' });
      document.getElementById('overall-summary-section')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    setIsSaving(true);
    try {
      // 1. Save to DHIS2
      await saveCheckIn(checkInData, CHECKIN_STATUS.SUBMITTED);
      
      setCheckInData(prev => (prev ? { ...prev, status: CHECKIN_STATUS.SUBMITTED } : prev));
      
      // 2. Trigger Email to Manager
      sendCheckInSubmittedEmail({
        staffId: user?.id || employeeId, 
        staffEmail: user?.email,
        staffName: checkInData.employeeName || `${user?.firstName || ''} ${user?.surname || ''}`.trim(),
        periodLabel: period === CHECKIN_PERIODS.Q1 ? 'Q1 (3 Months)' : 'Q2 (9 Months)',
        financialYear
      });

      setToast({ type: 'success', message: 'Check-in submitted successfully! Your manager has been notified.' });
      setTimeout(() => navigate('/check-in/dashboard'), 2000);
    } catch (error) { 
      console.error("Submission error:", error);
      setToast({ type: 'critical', message: 'Failed to submit check-in. Please try again.' }); 
    } finally { 
      setIsSaving(false); 
    }
  };  

    const handleManagerReview = async () => {
    if (!checkInData) return;

    // Validation: manager must fill both fields
    if (!checkInData.managerReviewComments || checkInData.managerReviewComments.trim() === '') {
      setToast({ type: 'warning', message: 'Manager Review: Please provide your Review Comments before completing the review.' });
      return;
    }
    if (!checkInData.managerReviewNextSteps || checkInData.managerReviewNextSteps.trim() === '') {
      setToast({ type: 'warning', message: 'Manager Review: Please provide Next Steps / Action Items before completing the review.' });
      return;
    }

    setIsSaving(true);
    try {
      // Complete Review now CLOSES the cycle -> COMPLETED
      await saveCheckIn(checkInData, CHECKIN_STATUS.COMPLETED);
      setCheckInData(prev => (prev ? { ...prev, status: CHECKIN_STATUS.COMPLETED } : prev));
      setToast({ type: 'success', message: 'Review completed. The check-in cycle is now closed.' });

      // Notify the staff member
      sendCheckInReviewedEmail({
        staffId: employeeId,
        staffName: checkInData.employeeName,
        managerName: `${user?.firstName || ''} ${user?.surname || ''}`.trim(),
        periodLabel: period === CHECKIN_PERIODS.Q1 ? 'Q1 (3 Months)' : 'Q2 (9 Months)',
        financialYear
      });
      refetch();
    } catch (error) {
      console.error('❌ Review save error:', error);
      setToast({ type: 'error', message: `Failed to save review: ${error.message || 'Unknown error'}` });
    } finally {
      setIsSaving(false);
    }
  };

  const handleScheduleMeeting = async () => {
    if (!checkInData) return;
    if (!meetingData.date) { setToast({ type: 'warning', message: 'Please select a meeting date and time' }); return; }
    setIsSaving(true);
    try {
      const updatedData = { ...checkInData, meetingRequired: true, meetingDate: meetingData.date, meetingNotes: meetingData.notes };
      await saveCheckIn(updatedData, CHECKIN_STATUS.MEETING_SCHEDULED);

      // Timeline flips to "Meeting Scheduled" immediately
      setCheckInData(prev => (prev ? {
        ...prev,
        status: CHECKIN_STATUS.MEETING_SCHEDULED,
        meetingRequired: true,
        meetingDate: meetingData.date,
        meetingNotes: meetingData.notes
      } : prev));

      setToast({ type: 'success', message: 'Meeting scheduled successfully!' });

      // TRrigger email
      sendMeetingScheduledEmail({
        staffEmail: checkInData.employeeEmail || user?.email,
        staffName: checkInData.employeeName,
        managerName: `${user?.firstName || ''} ${user?.surname || ''}`.trim(),
        periodLabel: period === CHECKIN_PERIODS.Q1 ? 'Q1 (3 Months)' : 'Q2 (9 Months)',
        financialYear,
        meetingDate: meetingData.date,
        meetingNotes: meetingData.notes
      });

      setShowMeetingScheduler(false);
      setMeetingData({ date: '', notes: '' });
      refetch();
    } catch (error) { setToast({ type: 'error', message: 'Failed to schedule meeting' }); } 
    finally { setIsSaving(false); }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case CHECKIN_STATUS.NOT_STARTED: return 'cif-status-badge--gray';
      case CHECKIN_STATUS.DRAFT: 
      case CHECKIN_STATUS.SUBMITTED: 
      case CHECKIN_STATUS.UNDER_REVIEW: return 'cif-status-badge--amber';
      case CHECKIN_STATUS.REVIEWED: 
      case CHECKIN_STATUS.MEETING_SCHEDULED: return 'cif-status-badge--blue';
      case CHECKIN_STATUS.COMPLETED: return 'cif-status-badge--green';
      default: return 'cif-status-badge--gray';
    }
  };

  if (fiscalYearLoading || loading || !isDataLoaded) {
    return (
      <div className="cif-page">
        <div className="cif-state cif-state--full">
          <div className="cif-spinner" />
          <p className="cif-state-text">Loading check-in form...</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="cif-page">
        <div className="cif-state cif-state--full">
          <div className="cif-state-icon cif-state-icon--red"><IconWarning16 /></div>
          <h3 className="cif-state-title">Error Loading Data</h3>
          <p className="cif-state-text">{error.message || 'Unknown error'}</p>
          <button onClick={() => { setIsDataLoaded(false); refetch(); }} className="cif-btn cif-btn--primary">Retry</button>
        </div>
      </div>
    );
  }

  // New permissions
  const canEdit = !isManagerView && [CHECKIN_STATUS.INITIATED, CHECKIN_STATUS.DRAFT, CHECKIN_STATUS.RETURNED].includes(checkInData.status);
  const canSubmit = canEdit;
  const canReview = isManagerView && checkInData.status === CHECKIN_STATUS.SUBMITTED;

  
  // Manager deep-link: initiate from the form without leaving the page
  const handleInitiateFromForm = async () => {
    try {
      await initiateCheckIn({ period, financialYear, quarter: period === CHECKIN_PERIODS.Q1 ? 1 : 2 });
      setToast({ type: 'success', message: 'Check-in initiated. Staff notified by email.' });
      setIsDataLoaded(false); refetch(); // reload with new status
    } catch { setToast({ type: 'error', message: 'Failed to initiate check-in' }); }
  };

    // ✅ 1. CHECK FOR NO OBJECTIVES FIRST
  if (!checkInData || !checkInData.objectives || checkInData.objectives.length === 0) {
    return (
      <div className="cif-page">
        <div className="cif-state cif-state--full">
          <div className="cif-state-icon cif-state-icon--amber"><GiNotebook /></div>
          <h3 className="cif-state-title">No Objectives Found</h3>
          <p className="cif-state-text">
            {isManagerView
              ? 'This staff member has no objectives assigned for this period.'
              : "You don't have any objectives assigned for this period. Please contact your manager or HR to assign objectives before starting your check-in."}
          </p>
          <button className="cif-btn cif-btn--outline" onClick={() => navigate('/check-in/dashboard')}>
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // ✅ 2. THEN CHECK FOR NOT INITIATED
  const isNotStarted = checkInData.status === CHECKIN_STATUS.NOT_STARTED;
  if (!isManagerView && isNotStarted) {
    return (
      <div className="cif-page">
        <div className="cif-state cif-state--full">
          <div className="cif-state-icon cif-state-icon--amber"><IconClock16 /></div>
          <h3 className="cif-state-title">Check-in Not Initiated Yet</h3>
          <p className="cif-state-text">
            Your manager has not opened your check-in for this period. You will receive an
            email notification as soon as it is ready for you to complete.
          </p>
          <button className="cif-btn cif-btn--outline" onClick={() => navigate('/check-in/dashboard')}>
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const isReadOnly = checkInData.status === CHECKIN_STATUS.SUBMITTED && !isManagerView;
  const statusConfig = STATUS_CONFIG[checkInData.status] || STATUS_CONFIG[CHECKIN_STATUS.NOT_STARTED];

    return (
    <div className="cif-page">
      {/* Added paddingBottom to prevent content from hiding behind the sticky bottom bar */}
      <div className="cif-container" style={{ paddingBottom: '100px' }}>
        
        <button style={{backgroundColor: '#2061c1', borderRadius: '18px', padding: '10px', color: 'white'}}
               onClick={() => navigate('/check-in/dashboard')}
               className="cif-back-btn">
          <IconArrowLeft16 /> Back to Dashboard
        </button>
        
        <div className="cif-header">
          <div className="cif-title-area">
            <h3>
              {isManagerView ? 'Review Check-in' : 'Check-in Form'}
              {isManagerView && <span className="cif-chip cif-chip--purple"><GiEyeball /> Manager View</span>}
            </h3>
            <div className="cif-tags">
              <span className="cif-chip cif-chip--blue"><IconCalendar16 /> {period === CHECKIN_PERIODS.Q1 ? 'Q1 (3 Months)' : 'Q2 (9 Months)'}</span>
              <span className="cif-chip cif-chip--gray">{financialYear}</span>
              <span className={`cif-status-badge ${getStatusBadgeClass(checkInData.status)}`}>
                <span className="cif-dot" /> <IconClock16 /> {statusConfig.label}
              </span>
              {isManagerView && <span className="cif-chip cif-chip--blue"><IconUserGroup16 /> Employee: {checkInData.employeeName || 'Staff'}</span>}
            </div>
          </div>
        </div>

        {/* STATUS TIMELINE — Horizontal Single Row */}
        <div className="cif-card cif-card--padded" style={{ marginBottom: '24px' }}>
          <h3 className="cif-card-title" style={{ marginBottom: '16px' }}>Status Timeline</h3>
          
          <div style={{ display: 'flex', alignItems: 'flex-start', width: '100%', overflowX: 'auto', padding: '8px 0 4px 0' }}>
            {[
              { key: 'initiated', label: 'Check-in Initiated', active: checkInData.status !== CHECKIN_STATUS.NOT_STARTED },
              { key: 'draft', label: 'Draft Saved', active: [CHECKIN_STATUS.DRAFT, CHECKIN_STATUS.SUBMITTED, CHECKIN_STATUS.UNDER_REVIEW, CHECKIN_STATUS.REVIEWED, CHECKIN_STATUS.MEETING_SCHEDULED, CHECKIN_STATUS.COMPLETED].includes(checkInData.status) },
              { key: 'submitted', label: 'Submitted', active: [CHECKIN_STATUS.SUBMITTED, CHECKIN_STATUS.UNDER_REVIEW, CHECKIN_STATUS.REVIEWED, CHECKIN_STATUS.MEETING_SCHEDULED, CHECKIN_STATUS.COMPLETED].includes(checkInData.status) },
              { key: 'reviewed', label: 'Reviewed', active: [CHECKIN_STATUS.REVIEWED, CHECKIN_STATUS.MEETING_SCHEDULED, CHECKIN_STATUS.COMPLETED].includes(checkInData.status) },
              { key: 'completed', label: 'Completed', active: checkInData.status === CHECKIN_STATUS.COMPLETED }
            ].map((step, index, arr) => {
              const GREEN = '#22a06b';
              const GRAY = '#d5dde8';
              const isFirst = index === 0;
              const isLast = index === arr.length - 1;
              const nextActive = !isLast && arr[index + 1].active;

              return (
                <div key={step.key} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: '110px' }}>
                  
                  {/* Dot + horizontal connector lines */}
                  <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    {/* Left line */}
                    <div style={{ flex: 1, height: '2px', backgroundColor: isFirst ? 'transparent' : (step.active ? GREEN : GRAY) }} />
                    {/* Dot */}
                    <div style={{
                      width: '18px',
                      height: '18px',
                      borderRadius: '50%',
                      backgroundColor: step.active ? GREEN : '#cbd5e1',
                      boxShadow: step.active ? '0 0 0 4px rgba(34,160,107,0.2)' : '0 0 0 4px rgba(203,213,225,0.35)',
                      flexShrink: 0
                    }} />
                    {/* Right line */}
                    <div style={{ flex: 1, height: '2px', backgroundColor: isLast ? 'transparent' : (nextActive ? GREEN : GRAY) }} />
                  </div>

                  {/* Label */}
                  <span style={{
                    marginTop: '10px',
                    fontSize: '0.85rem',
                    fontWeight: step.active ? 600 : 400,
                    color: step.active ? '#1f2937' : '#94a3b8',
                    textAlign: 'center',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}>
                    {step.label}
                    {step.active && <IconCheckmark16 style={{ color: GREEN }} />}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Status Alerts */}
        {isManagerView && checkInData.status === CHECKIN_STATUS.SUBMITTED && (
          <div className="cif-alert cif-alert--warning">
            <IconWarning16 /> <span>This check-in is pending your review. Please provide feedback and either complete the review or schedule a meeting.</span>
          </div>
        )}
        {!isManagerView && checkInData.status === CHECKIN_STATUS.SUBMITTED && (
          <div className="cif-alert cif-alert--success">
            <IconCheckmark16 /> <span>Your check-in has been submitted and is awaiting manager review.</span>
          </div>
        )}
        {checkInData.status === CHECKIN_STATUS.REVIEWED && (
          <div className="cif-alert cif-alert--success">
            <IconCheckmark16 /> <span>This check-in has been reviewed by your manager.</span>
          </div>
        )}
        {checkInData.status === CHECKIN_STATUS.MEETING_SCHEDULED && (
          <div className="cif-alert cif-alert--info" style={{ whiteSpace: 'pre-wrap', display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
            <IconCalendar16 style={{ marginTop: '4px', flexShrink: 0 }} />
            <div style={{ flex: 1 }}>
              <strong>A meeting has been scheduled to discuss this check-in.</strong>
              {checkInData.meetingDate && <div style={{ marginTop: '4px' }}>📅 <strong>Date:</strong> {new Date(checkInData.meetingDate).toLocaleString()}</div>}
              {checkInData.meetingNotes && <div style={{ marginTop: '4px' }}>📝 <strong>Agenda/Notes:</strong> {checkInData.meetingNotes}</div>}
            </div>
          </div>
        )}
        {checkInData.status === CHECKIN_STATUS.COMPLETED && (
          <div className="cif-alert cif-alert--success">
            <IconCheckmark16 /> <span>This check-in has been reviewed and completed. No further action is required.</span>
          </div>
        )}
        {isManagerView && isNotStarted && (
          <div className="cif-alert cif-alert--info">
            <IconWarning16 />
            <span>This check-in has not been initiated yet.</span>
            <button className="cif-btn cif-btn--primary cif-btn--sm" onClick={handleInitiateFromForm}>
              Initiate Check-in
            </button>
          </div>
        )}
        {isManagerView && (checkInData.status === CHECKIN_STATUS.INITIATED || checkInData.status === CHECKIN_STATUS.DRAFT) && (
          <div className="cif-alert cif-alert--info">
            <IconClock16 />
            <span>Waiting for {checkInData.employeeName || 'the staff member'} to complete and submit their self-assessment. You can only view their responses until then.</span>
          </div>
        )}

        {/* ✅ 2. SINGLE COLUMN LAYOUT (Removed cif-grid and cif-sidebar) */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          
          {/* Objectives Card */}
          <div className="cif-card">
            <div className="cif-card-head">
              <h2 className="cif-card-title"><GiNotebook /> Objectives <span className="cif-card-count">{checkInData.objectives.length}</span></h2>
              <div className="cif-card-subtitle">
                {expandedObjectiveId ? '1 objective expanded' : 'All collapsed'}
              </div>
            </div>
            <div className="cif-card-body">
              <div className="cif-objectives-list">
                {checkInData.objectives.map((objective, index) => (
                  <ObjectiveCard
                      key={objective.id || index}
                      objective={objective}
                      index={index}
                      isExpanded={expandedObjectiveId === objective.id}
                      onToggleExpand={() => toggleObjectiveExpand(objective.id)}
                      onChange={handleObjectiveChange}
                      isReadOnly={isReadOnly}
                      isManagerView={isManagerView}
                      managerCanEdit={canReview}
                      status={checkInData.status} 
                    />
                ))}
              </div>
            </div>
          </div>

          {/* Overall Summary */}
          <div className="cif-card cif-card--padded">
            <OverallSummary overall={checkInData.overall} onChange={handleOverallChange} isReadOnly={isReadOnly} isManagerView={isManagerView} />
          </div>

          {/* STAFF VIEW: Manager's overall review */}
          {!isManagerView && [CHECKIN_STATUS.REVIEWED, CHECKIN_STATUS.MEETING_SCHEDULED, CHECKIN_STATUS.COMPLETED].includes(checkInData.status) &&
            (checkInData.managerReviewComments || checkInData.managerReviewNextSteps) && (
            <div className="cif-card cif-card--padded" style={{ borderLeft: '4px solid #8e44ad' }}>
              <h3 className="cif-card-title" style={{ color: '#8e44ad' }}>📋 Manager's Overall Review</h3>
              {checkInData.managerReviewComments && (
                <div className="cc-field">
                  <label className="cc-label">Manager's Review Comments</label>
                  <div style={{ background:'#f8fafc', border:'1px solid #e2e8f0', borderRadius:8, padding:12, whiteSpace:'pre-wrap' }}>
                    {checkInData.managerReviewComments}
                  </div>
                </div>
              )}
              {checkInData.managerReviewNextSteps && (
                <div className="cc-field">
                  <label className="cc-label">Agreed Next Steps / Action Items</label>
                  <div style={{ background:'#f8fafc', border:'1px solid #e2e8f0', borderRadius:8, padding:12, whiteSpace:'pre-wrap' }}>
                    {checkInData.managerReviewNextSteps}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Manager Review Panel */}
          {isManagerView && (
            <div className="cif-card cif-card--padded">
              <ManagerReviewPanel
                checkInData={checkInData}
                onSaveReview={handleManagerReview}
                onScheduleMeeting={() => setShowMeetingScheduler(true)}
                onUpdateCheckIn={setCheckInData}
                isSaving={isSaving}
              />
            </div>
          )}

        </div>
      </div>

      {/* ✅ 3. STICKY BOTTOM ACTION BAR (Consolidated all buttons here) */}
      {( (isManagerView && canReview) || (!isManagerView && canEdit) ) && (
        <div style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: 'white',
          borderTop: '1px solid #e2e8f0',
          padding: '16px 24px',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '12px',
          zIndex: 100,
          boxShadow: '0 -4px 6px -1px rgba(0, 0, 0, 0.05)'
        }}>
          {isManagerView && canReview && (
            <>
              <button onClick={() => setShowMeetingScheduler(true)} className="cif-btn cif-btn--indigo">
                <IconCalendar16 /> Schedule Meeting
              </button>
              <button onClick={handleManagerReview} disabled={isSaving} className="cif-btn cif-btn--success">
                <IconCheckmark16 /> {isSaving ? 'Saving...' : 'Complete Review'}
              </button>
            </>
          )}
          {!isManagerView && canEdit && (
            <>
              <button 
                style={{backgroundColor: '#2061c1', borderRadius: '18px', padding: '10px', color: 'white'}}
                onClick={handleSaveDraft} 
                disabled={isSaving} 
                className="cif-btn cif-btn--outline">
                <IconSave16 /> {isSaving ? 'Saving...' : 'Save as Draft'}
              </button>
              <button
              style={{backgroundColor: '#0a9811', borderRadius: '18px', padding: '10px', color: 'white'}}
               onClick={handleSubmitFinal} disabled={isSaving} className="cif-btn cif-btn--primary">
                <IconCheckmark16 /> {isSaving ? 'Submitting...' : 'Submit Final'}
              </button>
            </>
          )}
        </div>
      )}

      {showMeetingScheduler && (
        <div className="cif-modal-overlay">
          <div className="cif-modal">
            <h3 className="cif-modal-title">Schedule Meeting</h3>
            <div className="cif-form-group">
              <label className="cif-label">Meeting Date & Time</label>
              <input type="datetime-local" className="cif-input" value={meetingData.date} onChange={(e) => setMeetingData({ ...meetingData, date: e.target.value })} />
            </div>
            <div className="cif-form-group">
              <label className="cif-label">Meeting Notes</label>
              <textarea rows={3} className="cif-textarea" placeholder="Agenda, topics to discuss..." value={meetingData.notes} onChange={(e) => setMeetingData({ ...meetingData, notes: e.target.value })} />
            </div>
            <div className="cif-modal-actions">
              <button onClick={() => { setShowMeetingScheduler(false); setMeetingData({ date: '', notes: '' }); }} className="cif-btn cif-btn--outline">Cancel</button>
              <button onClick={handleScheduleMeeting} disabled={isSaving} className="cif-btn cif-btn--primary">
                {isSaving ? 'Scheduling...' : 'Schedule Meeting'}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && <ToastNotification type={toast.type} message={toast.message} onClose={() => setToast(null)} />}

      {/* FIXED ALERT BAR - Pins to top center of screen */}
      {toast && (
        <div style={{
          position: 'fixed',
          top: '24px',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 9999,
          width: '90%',
          maxWidth: '600px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
        }}>
          <AlertBar
            success={toast.type === 'success'}
            warning={toast.type === 'warning'}
            critical={toast.type === 'critical' || toast.type === 'error'}
            duration={6000}
            onHidden={() => setToast(null)}
          >
            {toast.message}
          </AlertBar>
        </div>
      )}
    </div>
  );
};

export default CheckInForm;